/* eslint-disable no-dupe-class-members */

import type FiltersOptions from '../../api/typedefs/sdk/PaginationOptions';
import type { RequestExecute } from '../../api';
import globals from '../../globals';

import type { ClientApiInterface } from '../../..';
import type Request from '../../api/types/RequestInterface';
import type ResponseBody from '../../api/types/ResponseBody';

/**
 * @class Helper
 * @private
 * @desc Abstract class for helpers.
 * @property {ClientApi} api Client used for handling requests.
 * @property {String} uri URI used by the helper.
 */
class Helper<EntityType extends ResponseBody | void = void> {
  api: ClientApiInterface;

  uri: string;

  protected subItemsHelpers: Record<string, typeof Helper> = {};

  /**
   * @method constructor
   * @private
   * @param {ClientApi} api Client used for handling requests.
   * @param {String} uri URI used by the helper.
   * @example const helper = new Helper(api, '/my_path');
   */
  constructor(api: ClientApiInterface, uri: string) {
    this.api = api;
    this.uri = uri;
  }

  id(id: number | string): Record<string, Helper<EntityType>> {
    const subHelperUri = this.uri.replace(/\/:id/, `/${id}`);

    return Object.keys(this.subItemsHelpers).reduce(
      (helpers, helperName) => ({
        ...helpers,
        [helperName]: new this.subItemsHelpers[helperName](this.api, subHelperUri),
      }),
      {}
    );
  }

  /**
   * @method find
   * @public
   * @desc Search for some items with given parameters.
   * @param {FiltersOptions} [filters={}] Filters to use to find items.
   * @param {Boolean} [directlyExecute=true]
   * Tells if the generated request should be directly executed.
   * @return {Promise|Request}
   * The promise handling the request if `directlyExecute` is `true`.
   * The generated Request object if `directlyExecute` is `false`.
   * @example helper.find({ foo: 'bar' });
   * @example helper.find({ foo: 'bar', start: 0, end: 9 });
   * @example
   * helper.find({ foo: 'bar', start: 0, end: 9 }).then((data) => {
   *  // Fetch next page if needed
   *  data.pagination.next().then((newPage) => {
   *    // ...
   *  });
   * });
   */
  find(filters?: FiltersOptions): RequestExecute<EntityType[]>;

  find(filters: FiltersOptions, directlyExecute: false): Request<EntityType[]>;

  find(filters: FiltersOptions, directlyExecute: true): RequestExecute<EntityType[]>;

  find(
    filters: FiltersOptions = {},
    directlyExecute: boolean = true
  ): Request<EntityType[]> | RequestExecute<EntityType[]> {
    const { start, end, headers, ...requestParams } = filters;

    const request = this.api
      .createGetRequest<EntityType[]>(this.uri)
      .setQueryParams(requestParams)
      .setHeaders(headers || {})
      .paginate(start, end);

    return directlyExecute ? request.execute() : request;
  }

  /**
   * @method get
   * @public
   * @desc Get a specific item from its ID.
   * @param {Number} itemId The ID of the item to get.
   * @param {Boolean} [directlyExecute=true]
   * Tells if the generated request should be directly executed.
   * @return {Promise|Request}
   * The promise handling the request if `directlyExecute` is `true`.
   * The generated Request object if `directlyExecute` is `false`.
   * @example helper.get(123);
   */

  get(itemId: number | string): RequestExecute<EntityType>;

  get(itemId: number | string, directlyExecute: false): Request<EntityType>;

  get(itemId: number | string, directlyExecute: true): RequestExecute<EntityType>;

  get(
    itemId: number | string,
    directlyExecute: boolean = true
  ): Request<EntityType> | RequestExecute<EntityType> {
    const request = this.api
      .createGetRequest<EntityType>(this.uri)
      .setRouteParam('id', `${itemId}`);

    return directlyExecute ? request.execute() : request;
  }

  /**
   * @method create
   * @public
   * @desc Create an item with given parameters.
   * @param {Object} params Params to use for creating the new item.
   * @param {Boolean} [directlyExecute=true]
   * Tells if the generated request should be directly executed.
   * @return {Promise|Request}
   * The promise handling the request if `directlyExecute` is `true`.
   * The generated Request object if `directlyExecute` is `false`.
   * @example helper.create({ foo: 'bar' });
   */
  create<
    RequestBodyType = Partial<EntityType>,
    ResponseBodyType extends ResponseBody | void = Partial<EntityType>
  >(params: RequestBodyType): RequestExecute<ResponseBodyType>;

  create<
    RequestBodyType = Partial<EntityType>,
    ResponseBodyType extends ResponseBody | void = Partial<EntityType>
  >(params: RequestBodyType, directlyExecute: false): Request<ResponseBodyType>;

  create<
    RequestBodyType = Partial<EntityType>,
    ResponseBodyType extends ResponseBody | void = Partial<EntityType>
  >(params: RequestBodyType, directlyExecute: true): RequestExecute<ResponseBodyType>;

  create<
    RequestBodyType = Partial<EntityType>,
    ResponseBodyType extends ResponseBody | void = Partial<EntityType>
  >(
    params: RequestBodyType,
    directlyExecute: boolean = true
  ): Request<ResponseBodyType> | RequestExecute<ResponseBodyType> {
    const request = this.api.createPostRequest<ResponseBodyType>(this.uri).setBodyParams(params);

    return directlyExecute ? request.execute() : request;
  }

  /**
   * @method delete
   * @public
   * @desc Delete an item by its id.
   * @param {Number} itemId The ID of the item to delete.
   * @param {Boolean} [directlyExecute=true]
   * Tells if the generated request should be directly executed.
   * @return {Promise|Request}
   * The promise handling the request if `directlyExecute` is `true`.
   * The generated Request object if `directlyExecute` is `false`.
   * @example helper.delete(1234);
   */
  delete(itemId: number | string): RequestExecute<void>;

  delete(itemId: number | string, directlyExecute: false): Request<void>;

  delete(itemId: number | string, directlyExecute: true): RequestExecute<void>;

  delete(
    itemId: number | string,
    directlyExecute: boolean = true
  ): Request<void> | RequestExecute<void> {
    const request = this.api.createDeleteRequest(this.uri).setRouteParam('id', `${itemId}`);

    return directlyExecute ? request.execute() : request;
  }

  /**
   * @method update
   * @public
   * @desc Update an item with given parameters.
   * @param {Number} itemId The ID of the item to update.
   * @param {Object} params Params to use for updating the item.
   * @param {Boolean} [directlyExecute=true]
   * Tells if the generated request should be directly executed.
   * @return {Promise|Request}
   * The promise handling the request if `directlyExecute` is `true`.
   * The generated Request object if `directlyExecute` is `false`.
   * @example helper.update(123, { foo: 'bar' });
   */
  update(itemId: number, params: Partial<EntityType>): RequestExecute<EntityType>;

  update(itemId: number, params: Partial<EntityType>, directlyExecute: false): Request<EntityType>;

  update(
    itemId: number,
    params: Partial<EntityType>,
    directlyExecute: true
  ): RequestExecute<EntityType>;

  update(
    itemId: number,
    params: Partial<EntityType>,
    directlyExecute: boolean = true
  ): Request<EntityType> | RequestExecute<EntityType> {
    const request = this.api
      .createPutRequest<EntityType>(this.uri)
      .setRouteParam('id', `${itemId}`)
      .setBodyParams(params);

    return directlyExecute ? request.execute() : request;
  }

  /**
   * @method partialUpdate
   * @public
   * @desc Update partially an item with given parameters.
   * @param {Number} itemId The ID of the item to update.
   * @param {Object} params Params to use for updating the item.
   * @param {Boolean} [directlyExecute=true]
   * Tells if the generated request should be directly executed.
   * @return {Promise|Request}
   * The promise handling the request if `directlyExecute` is `true`.
   * The generated Request object if `directlyExecute` is `false`.
   * @example helper.partialUpdate(123, { foo: 'bar' });
   */
  partialUpdate<RequestBodyType = Partial<EntityType>, ResponseBodyType = Partial<EntityType>>(
    itemId: string | number,
    params: RequestBodyType
  ): RequestExecute<Partial<ResponseBodyType>>;

  partialUpdate<RequestBodyType = Partial<EntityType>, ResponseBodyType = Partial<EntityType>>(
    itemId: string | number,
    params: RequestBodyType,
    directlyExecute: false
  ): Request<Partial<ResponseBodyType>>;

  partialUpdate<RequestBodyType = Partial<EntityType>, ResponseBodyType = Partial<EntityType>>(
    itemId: string | number,
    params: RequestBodyType,
    directlyExecute: true
  ): RequestExecute<Partial<ResponseBodyType>>;

  partialUpdate<
    RequestBodyType = Partial<EntityType>,
    ResponseBodyType extends ResponseBody | void = Partial<EntityType>
  >(
    itemId: string | number,
    params: RequestBodyType,
    directlyExecute: boolean = true
  ): Request<Partial<ResponseBodyType>> | RequestExecute<Partial<ResponseBodyType>> {
    const request = this.api
      .createPatchRequest<ResponseBodyType>(this.uri)
      .setRouteParam('id', `${itemId}`)
      .setBodyParams(params);

    return directlyExecute ? request.execute() : request;
  }

  /**
   * @method upload
   * @public
   * @desc Upload some files.
   * @param {File|Array<File>} files File object or array of files to upload.
   * @param {Function} [onProgress=null] Callback for onprogress event
   * @param {Boolean} [directlyExecute=true]
   * Tells if the generated request should be directly executed.
   * @return {Promise|Request}
   * The promise handling the request if `directlyExecute` is `true`.
   * The generated Request object if `directlyExecute` is `false`.
   * @example helper.upload(MyFile);
   */
  upload(files: File | File[], onProgress: Function | null): RequestExecute<any>;

  upload(files: File | File[], onProgress: Function | null, directlyExecute: false): Request<any>;

  upload(
    files: File | File[],
    onProgress: Function | null,
    directlyExecute: true
  ): RequestExecute<any>;

  upload(
    files: File | File[],
    onProgress: Function | null = null,
    directlyExecute: boolean = true
  ): Request<any> | RequestExecute<any> {
    const filesToUpload = Array.isArray(files) ? files : [files];
    const totalFiles = filesToUpload.length;
    // @ts-ignore
    const formData = new globals.window.FormData();

    filesToUpload.forEach((file, i) => {
      const filename = totalFiles === 1 ? 'uploaded_file' : `uploaded_files[${i}]`;
      formData.append(filename, file);
    });

    const request = this.api
      .createPostRequest(this.uri)
      .setRawBody(formData)
      .setHeader('Content-Type', undefined);

    if (onProgress) {
      request.setUploadOption('progress', onProgress);
    }

    return directlyExecute ? request.execute() : request;
  }
}

export default Helper;
